import { gsap } from 'gsap';

/**
 * ═══════════════════════════════════════════════════════════════════════
 *  STICKY MAGNETIC BUTTONS — sticky-btn.component.js
 * ═══════════════════════════════════════════════════════════════════════
 *
 *  Маркеры в HTML:
 *  ─────────────────────────────────────────────────────────────────────
 *  [data-sticky-target]          — элемент, который притягивается
 *  [data-sticky-group]           — родитель: все дочерние target-ы
 *                                   магнитятся одновременно независимо
 *
 *  Переопределение настроек через data-атрибуты:
 *  ─────────────────────────────────────────────────────────────────────
 *  На [data-sticky-target]:
 *    data-sticky-zone="40"       — px от края, с которого начинается притяжение
 *    data-sticky-neighbor="3"    — px — зона в сторону соседа (solo-режим)
 *    data-sticky-strength="0.7"  — сила смещения (0 = нет, 1 = полное)
 *    data-sticky-duration="0.4"  — длительность анимации GSAP (сек)
 *
 *  На [data-sticky-group]:
 *    data-sticky-zone="20"       — зона притяжения для всей группы
 *    data-sticky-strength="0.3"  — сила для всей группы
 *    data-sticky-duration="0.5"  — длительность для всей группы
 *
 *  Пример:
 *    <button data-sticky-target data-sticky-zone="50" data-sticky-strength="0.8">
 *    <div data-sticky-group data-sticky-zone="10" data-sticky-strength="0.2">
 * ═══════════════════════════════════════════════════════════════════════
 */

const isTouchDevice = () =>
  'ontouchstart' in window || navigator.maxTouchPoints > 0;

const DESKTOP_BREAKPOINT = 1033; // px — ниже и включительно анимация не работает

// ── Дефолтные настройки: одиночный режим ────────────────────────────────
const MAGNETIC_ZONE = 30;   // px от края, с которого начинается притяжение
const NEIGHBOR_ZONE = 5;    // px — зона в сторону соседнего элемента
const MOVE_STRENGTH = 0.5;  // сила притяжения (0 = нет, 1 = полное)
const DURATION = 0.6;  // длительность анимации GSAP (сек)
const EASE = 'power2.out';

// ── Дефолтные настройки: групповой режим (data-sticky-group) ────────────
const GROUP_MAGNETIC_ZONE = 15;   // px — зона для группы
const GROUP_MOVE_STRENGTH = 0.25; // слабее притяжение
// ─────────────────────────────────────────────────────────────────────────

// Хелпер: читает число из dataset, иначе возвращает fallback
function ds(el, key, fallback) {
  const v = parseFloat(el.dataset[key]);
  return isNaN(v) ? fallback : v;
}

function getDirectionalZone(target, rect, allTargets) {
  const neighborZone = ds(target, 'stickyNeighbor', NEIGHBOR_ZONE);
  const magneticZone = ds(target, 'stickyZone', MAGNETIC_ZONE);

  let zoneLeft = magneticZone;
  let zoneRight = magneticZone;
  let zoneTop = magneticZone;
  let zoneBottom = magneticZone;

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  allTargets.forEach((other) => {
    if (other === target) return;

    const r = other.getBoundingClientRect();
    const ocx = r.left + r.width / 2;
    const ocy = r.top + r.height / 2;

    const dx = ocx - cx;
    const dy = ocy - cy;

    if (Math.abs(dx) >= Math.abs(dy)) {
      if (dx > 0) zoneRight = neighborZone;
      else zoneLeft = neighborZone;
    } else {
      if (dy > 0) zoneBottom = neighborZone;
      else zoneTop = neighborZone;
    }
  });

  return { zoneLeft, zoneRight, zoneTop, zoneBottom };
}

function isInZone(mouseX, mouseY, rect, zone) {
  return (
    mouseX >= rect.left - zone.zoneLeft &&
    mouseX <= rect.right + zone.zoneRight &&
    mouseY >= rect.top - zone.zoneTop &&
    mouseY <= rect.bottom + zone.zoneBottom
  );
}

function resetAllTargets() {
  document.querySelectorAll('[data-sticky-target]').forEach((target) => {
    gsap.killTweensOf(target);
    gsap.set(target, { x: 0, y: 0, scale: 1, zIndex: 'auto', clearProps: 'zIndex' });
  });
}

function shouldRun() {
  return !isTouchDevice() && window.innerWidth > DESKTOP_BREAKPOINT;
}

/**
 * Вычисляет нормализованное смещение по X и Y.
 *
 * Проблема: элементы намного шире, чем высота. При одинаковом расстоянии
 * мыши от края смещение по X оказывалось пропорционально больше, потому что
 * rawX = mouseX − centerX может быть вдвое-втрое больше rawY = mouseY − centerY
 * уже внутри зоны притяжения.
 *
 * Решение: нормализуем сырой сдвиг относительно половины соответствующей
 * стороны элемента ([-1..1]), а затем умножаем обе оси на одну и ту же
 * базу — наименьшую из полуширины/полувысоты. Это даёт симметричное
 * смещение независимо от пропорций элемента.
 */
function calcMagneticMove(mouseX, mouseY, rect, strength) {
  const halfW = rect.width / 2;
  const halfH = rect.height / 2;
  const base = Math.min(halfW, halfH); // одна база для обеих осей

  const normX = (mouseX - (rect.left + halfW)) / halfW; // [-1..1]
  const normY = (mouseY - (rect.top + halfH)) / halfH; // [-1..1]

  return {
    moveX: normX * base * strength,
    moveY: normY * base * strength,
  };
}

window.initStickyBtns = function initStickyBtns() {
  if (!shouldRun()) return;

  const allTargets = Array.from(document.querySelectorAll('[data-sticky-target]'));
  if (!allTargets.length) return;

  const soloTargets = allTargets.filter((t) => !t.closest('[data-sticky-group]'));
  const groupParents = Array.from(document.querySelectorAll('[data-sticky-group]'));

  // ── Одиночный режим ───────────────────────────────────────────────────
  let activeTarget = null;

  document.addEventListener('mousemove', (e) => {
    const mouseX = e.clientX;
    const mouseY = e.clientY;

    soloTargets.forEach((target) => {
      const rect = target.getBoundingClientRect();
      const zone = getDirectionalZone(target, rect, soloTargets);
      const inZone = isInZone(mouseX, mouseY, rect, zone);
      const isActive = activeTarget === target;
      const isBlocked = activeTarget !== null && !isActive;

      if (inZone && !isBlocked) {
        if (!isActive) {
          activeTarget = target;
          gsap.set(target, { zIndex: 10 });
        }

        const strength = ds(target, 'stickyStrength', MOVE_STRENGTH);
        const duration = ds(target, 'stickyDuration', DURATION);
        const { moveX, moveY } = calcMagneticMove(mouseX, mouseY, rect, strength);

        gsap.to(target, { x: moveX, y: moveY, duration, ease: EASE, overwrite: 'auto' });
      } else if (isActive && !inZone) {
        activeTarget = null;
        const duration = ds(target, 'stickyDuration', DURATION);
        gsap.to(target, { x: 0, y: 0, zIndex: 'auto', duration, ease: EASE, overwrite: 'auto' });
      }
    });

    // ── Групповой режим ─────────────────────────────────────────────────
    groupParents.forEach((group) => {
      const magneticZone = ds(group, 'stickyZone', GROUP_MAGNETIC_ZONE);
      const strength = ds(group, 'stickyStrength', GROUP_MOVE_STRENGTH);
      const duration = ds(group, 'stickyDuration', DURATION);

      const groupTargets = Array.from(group.querySelectorAll('[data-sticky-target]'));

      groupTargets.forEach((target) => {
        const rect = target.getBoundingClientRect();
        const zone = { zoneLeft: magneticZone, zoneRight: magneticZone, zoneTop: magneticZone, zoneBottom: magneticZone };
        const inZone = isInZone(mouseX, mouseY, rect, zone);

        if (inZone) {
          const { moveX, moveY } = calcMagneticMove(mouseX, mouseY, rect, strength);

          gsap.set(target, { zIndex: 10 });
          gsap.to(target, { x: moveX, y: moveY, duration, ease: EASE, overwrite: 'auto' });
        } else {
          gsap.set(target, { zIndex: 'auto' });
          gsap.to(target, { x: 0, y: 0, duration, ease: EASE, overwrite: 'auto' });
        }
      });
    });
  });

  // ── Press-эффект для btn-элементов ────────────────────────────────────
  allTargets.forEach((target) => {
    if (!target.classList.item(0)?.startsWith('btn')) return;

    target.addEventListener('mousedown', () => {
      gsap.to(target, { scale: 0.96, duration: 0.08, ease: 'power2.in' });
    });

    const releasePress = () => {
      gsap.to(target, { scale: 1, duration: 0.35, ease: 'power2.out' });
    };

    target.addEventListener('mouseup', releasePress);
    target.addEventListener('mouseleave', releasePress);
  });
};

initStickyBtns();

// Ресайз: реинициализация при пересечении брейкпоинта
let _wasDesktop = shouldRun();
let _resizeTimer;

window.addEventListener('resize', () => {
  clearTimeout(_resizeTimer);
  _resizeTimer = setTimeout(() => {
    const isDesktop = shouldRun();
    if (_wasDesktop === isDesktop) return;
    _wasDesktop = isDesktop;

    if (!isDesktop) {
      resetAllTargets();
    } else {
      initStickyBtns();
    }
  }, 150);
});